import { WebGLContext } from './WebGLContext';
import { WebGLUtils } from './WebGLUtils';
import { SimulationConfig } from '@/types';

const QUAD_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 a_position;
out vec2 v_uv;

void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const TRAILS_FRAGMENT_SHADER = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 fragColor;

uniform sampler2D u_trailTexture;
uniform float u_fade;
uniform float u_blurStrength;
uniform vec2 u_resolution;

void main() {
    // Elegant 5-tap Gaussian-like box blur to diffuse vector laser trails into soft glowing rivers
    vec2 offset = u_blurStrength / u_resolution;
    vec4 current = texture(u_trailTexture, v_uv);
    vec4 up = texture(u_trailTexture, v_uv + vec2(0.0, offset.y));
    vec4 down = texture(u_trailTexture, v_uv - vec2(0.0, offset.y));
    vec4 left = texture(u_trailTexture, v_uv - vec2(offset.x, 0.0));
    vec4 right = texture(u_trailTexture, v_uv + vec2(offset.x, 0.0));
    
    vec4 average = (current * 2.5 + up + down + left + right) / 6.5;
    
    // Apply decay coefficient
    fragColor = average * u_fade;
}
`;

const DRAW_FRAGMENT_SHADER = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 fragColor;

uniform sampler2D u_texture;

void main() {
    fragColor = texture(u_texture, v_uv);
}
`;

export class GPUPingPongTrails {
  private program: WebGLProgram;
  private drawProgram: WebGLProgram;
  private quadVao: WebGLVertexArrayObject;
  private quadVbo: WebGLBuffer;

  private framebuffers: WebGLFramebuffer[] = [];
  private textures: WebGLTexture[] = [];
  private activeIndex = 0; // Current source text pointer index
  
  private width = 0;
  private height = 0;

  constructor(private context: WebGLContext) {
    const gl = context.gl;

    // Compile shader programs
    this.program = WebGLUtils.createProgram(gl, QUAD_VERTEX_SHADER, TRAILS_FRAGMENT_SHADER);
    this.drawProgram = WebGLUtils.createProgram(gl, QUAD_VERTEX_SHADER, DRAW_FRAGMENT_SHADER);

    // Flat quad vertices mapping full NDC space [-1.0, 1.0]
    const vertices = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
      -1,  1,
       1, -1,
       1,  1,
    ]);

    this.quadVao = gl.createVertexArray()!;
    gl.bindVertexArray(this.quadVao);

    this.quadVbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVbo);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    gl.bindVertexArray(null);

    this.reallocateTextures();
  }

  resize(width: number, height: number) {
    if (this.width === width && this.height === height) return;
    this.width = width;
    this.height = height;
    this.reallocateTextures();
  }

  private reallocateTextures() {
    const gl = this.context.gl;
    const w = Math.max(1, this.width || this.context.width);
    const h = Math.max(1, this.height || this.context.height);

    // Delete obsolete buffers
    this.framebuffers.forEach(fb => gl.deleteFramebuffer(fb));
    this.textures.forEach(tex => gl.deleteTexture(tex));
    this.framebuffers = [];
    this.textures = [];

    for (let i = 0; i < 2; i++) {
      const tex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

      const fb = gl.createFramebuffer()!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);

      const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
      if (status !== gl.FRAMEBUFFER_COMPLETE) {
        console.error('Frame buffer initialization returned status code:', status);
      }

      this.textures.push(tex);
      this.framebuffers.push(fb);
    }

    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  bindTarget() {
    const gl = this.context.gl;
    const targetIdx = 1 - this.activeIndex;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers[targetIdx]);
    gl.viewport(0, 0, this.width || this.context.width, this.height || this.context.height);
  }

  renderPulseAndDecay(config: SimulationConfig) {
    const gl = this.context.gl;
    const w = this.width || this.context.width;
    const h = this.height || this.context.height;

    const sourceIdx = this.activeIndex;
    const targetIdx = 1 - this.activeIndex;

    // Draw decaying copy of source frame into target frame
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers[targetIdx]);
    gl.viewport(0, 0, w, h);

    // Use trails program to decay-blur source texture into target framebuffer
    this.context.useProgram(this.program);
    gl.bindVertexArray(this.quadVao);

    // Default configuration metrics
    const decay = config.trailPersistence ? 1.0 : 0.94;
    const blur = config.trailPersistence ? 0.0 : 0.55; // Diffusion width multiplier

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures[sourceIdx]);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_trailTexture'), 0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_fade'), decay);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_blurStrength'), blur);
    gl.uniform2f(gl.getUniformLocation(this.program, 'u_resolution'), w, h);

    // Disable blend when copying base trails to prevent exponential saturation
    gl.disable(gl.BLEND);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.enable(gl.BLEND); // Re-enable standard composition blend

    gl.bindVertexArray(null);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  drawToScreen() {
    const gl = this.context.gl;
    const w = this.width || this.context.width;
    const h = this.height || this.context.height;

    const targetIdx = 1 - this.activeIndex;

    // Draw the completed target texture onto screen
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);

    this.context.useProgram(this.drawProgram);
    gl.bindVertexArray(this.quadVao);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures[targetIdx]);
    gl.uniform1i(gl.getUniformLocation(this.drawProgram, 'u_texture'), 0);

    // Blend trails cleanly on top of the black canvas clear color/background contents
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // Proper pre-multiplied alpha layout

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA); // Reset blend style
    gl.bindVertexArray(null);
    gl.bindTexture(gl.TEXTURE_2D, null);

    // Finalize state transition sync
    this.activeIndex = targetIdx;
  }

  reset() {
    const gl = this.context.gl;
    const w = this.width || this.context.width;
    const h = this.height || this.context.height;

    this.framebuffers.forEach(fb => {
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    });
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.activeIndex = 0;
  }
}
